import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Bookmark, Inbox } from "lucide-react";
import { Header } from "@/components/homepage/Header";
import { Footer } from "@/components/homepage/Footer";
import { OpportunityCard } from "@/components/homepage/OpportunityCard";
import { EmptyState } from "@/components/EmptyState";
import { getSavedIds } from "@/utils/savedOpportunities";
import { useOpportunities } from "@/hooks/useOpportunities";
import { useAuth } from "@/context/AuthContext";
import * as authApi from "@/services/authService";

export default function SavedPage() {
  const { opportunities, isLoading: oppsLoading } = useOpportunities();
  const [ids, setIds] = useState(() => getSavedIds());
  const [syncing, setSyncing] = useState(false);
  const { user, isLoading: authLoading } = useAuth();

  // Re-sync every time either the auth state or the local list changes.
  useEffect(() => {
    let cancelled = false;
    async function sync() {
      if (authLoading) return;
      const local = getSavedIds();
      if (!user) {
        if (!cancelled) setIds(local);
        return;
      }
      setSyncing(true);
      try {
        if (local.length) { await authApi.backfillSaved(local); }
        const { public_ids: server } = await authApi.listSaved();
        if (!cancelled) {
          const merged = Array.from(new Set([...(server || []), ...local]));
          setIds(merged);
          // Local list becomes a mirror of the server (no duplicates).
          try { window.localStorage.setItem("fursa.savedOpportunities.v1", JSON.stringify(server || [])); }
          catch { /* quota */ }
        }
      } catch {
        if (!cancelled) setIds(local);
      } finally {
        if (!cancelled) setSyncing(false);
      }
    }
    sync();
    const onStorage = () => setIds(getSavedIds());
    window.addEventListener("storage", onStorage);
    return () => { cancelled = true; window.removeEventListener("storage", onStorage); };
  }, [user?.id, authLoading]);

  const saved = ids
    .map((id) => opportunities.find((o) => o.id === id))
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-surface-50 text-ink-900">
      <Header />
      <main data-testid="saved-page">
        <section className="border-b border-surface-200 bg-white">
          <div className="mx-auto max-w-7xl px-6 pt-10 pb-8 lg:px-8 lg:pt-14 lg:pb-12">
            <p className="text-xs font-medium uppercase tracking-wider text-ink-500">Saved</p>
            <h1 data-testid="saved-title" className="mt-2 flex items-center gap-3 text-3xl font-bold tracking-tight text-ink-900 sm:text-4xl">
              <Bookmark className="h-8 w-8 text-intel-800" strokeWidth={1.75} />
              الفرص المحفوظة
            </h1>
            <p className="mt-3 text-base leading-relaxed text-ink-500">
              الفرص التي حفظتها للرجوع إليها لاحقاً — <span dir="ltr" className="font-mono font-semibold text-intel-800">{saved.length}</span> فرصة.
            </p>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-6 py-10 lg:px-8 lg:py-14">
          {saved.length === 0 ? (
            <EmptyState
              testId="saved-empty"
              icon={Inbox}
              title="لا فرص محفوظة بعد"
              description={`استخدم زر "حفظ" داخل صفحة أي فرصة لإضافتها هنا والرجوع إليها لاحقاً.`}
              ctaLabel="استكشف الفرص"
              ctaHref="/opportunities"
            />
          ) : (
            <div data-testid="saved-grid" className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {saved.map((o) => (
                <OpportunityCard key={o.id} opportunity={o} />
              ))}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}
